import React from 'react';
import { motion } from "motion/react";
import type { Event } from '@/features/clubs/types/types';
import { getSignedMediaUrl } from '@/lib/utils';
import { CalendarIcon } from './icons/ClubPageIcons';
import ImagePlaceholder from './ImagePlaceholder';
import LikeButton from '@/components/LikeButton';

interface EventCardProps {
  event: Event;
  onEventClick?: (event: Event) => void;
  isLiked?: boolean;
  onToggleLike?: (newState: boolean) => void;
}

const EventCard: React.FC<EventCardProps> = ({ event, onEventClick, isLiked, onToggleLike }) => {
  // Variants for orchestrating the hover animations on child elements
  const cardVariants = {
    initial: {},
    hover: {}, // State to trigger children animations
  };

  const imageVariants = {
    initial: { scale: 1 },
    hover: { scale: 1.05, transition: { duration: 0.5, ease: "easeOut" as const } },
  };

  const arrowVariants = {
    initial: { opacity: 0, x: -25 },
    hover: { opacity: 1, x: 0, transition: { duration: 0.4, ease: [0.43, 0.13, 0.23, 0.96] as const } },
  };

  const textVariants = {
    initial: { x: 0 },
    hover: { x: 36, transition: { duration: 0.4, ease: [0.43, 0.13, 0.23, 0.96] as const } },
  };

  // Convert Event to display format
  const getEventSchedule = (event: Event) => {
    if (event.event_type === 'REPEATED' && event.repeated_schedule) {
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayNames = event.repeated_schedule.daily_times
        .map(time => days[time.day_of_week])
        .join(', ');
      return dayNames;
    }
    return 'Check schedule';
  };

  const getFeaturedImage = (event: Event) => {
    // First try card_image_id, then fall back to media array
    if (event.card_image_id && event.card_image_id !== '00000000-0000-0000-0000-000000000000') {
      return event.card_image_id;
    }
    if (event.media && event.media.length > 0) {
      return event.media[0].media_id;
    }
    return `${event.name} Event`;
  };

  const renderEventMedia = (event: Event) => {
    // Check if we have a card_image_id first
    if (event.card_image_id && event.card_image_id !== '00000000-0000-0000-0000-000000000000') {
      // Check if it's a video (do we need better video detection logic?)
      if (event.card_image_id.includes('video') || event.card_image_id.includes('mp4')) {
        return (
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLVideoElement;
              target.style.display = 'none';
              target.nextElementSibling?.classList.remove('hidden');
            }}
          >
            <source src={getSignedMediaUrl(event.card_image_id, 'video').url} type="video/mp4" />
            <source src={getSignedMediaUrl(event.card_image_id, 'video').url} type="video/webm" />
            Your browser does not support the video tag.
          </video>
        );
      } else {
        return (
          <img
            src={getSignedMediaUrl(event.card_image_id, 'image').url}
            alt={event.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.nextElementSibling?.classList.remove('hidden');
            }}
          />
        );
      }
    }
    
    // Fall back to media array if no card_image_id
    if (event.media && event.media.length > 0) {
      const mediaId = event.media[0].media_id;
      // Check if it's a video (do we need better video detection logic?)
      if (mediaId.includes('video') || mediaId.includes('mp4')) {
        return (
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLVideoElement;
              target.style.display = 'none';
              target.nextElementSibling?.classList.remove('hidden');
            }}
          >
            <source src={getSignedMediaUrl(mediaId, 'video').url} type="video/mp4" />
            <source src={getSignedMediaUrl(mediaId, 'video').url} type="video/webm" />
            Your browser does not support the video tag.
          </video>
        );
      } else {
        return (
          <img
            src={getSignedMediaUrl(mediaId, 'image').url}
            alt={event.name}
            className="w-full h-full object-cover"
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.nextElementSibling?.classList.remove('hidden');
            }}
          />
        );
      }
    }
    return null;
  };

  return (
    <motion.div
      onClick={() => onEventClick?.(event)}
      className="cursor-pointer block transform-gpu"
      variants={cardVariants}
      initial="initial"
      whileHover="hover"
      style={{ transformOrigin: "center" }}
    >
      {/* Image container with rounded corners and hover effect */}
      <div className="mb-4 overflow-hidden rounded-lg shadow-lg relative">
        <motion.div 
          className="relative w-full aspect-[4/3]"
          variants={imageVariants}
        >
          {renderEventMedia(event)}
          <ImagePlaceholder label={getFeaturedImage(event)} className="hidden" />
        </motion.div>
        
        {/* Like Button - Top Right */}
        {onToggleLike && (
          <LikeButton
            entityId={event.id}
            entityType="events"
            isLiked={isLiked || false}
            onToggle={onToggleLike}
            className="absolute top-3 right-3 z-10"
            size="md"
          />
        )}
      </div>
      {/* Text content for the card */}
      <div>
        <div className="flex items-center gap-2 text-xs font-semibold tracking-widest text-gray-400 uppercase mb-2">
          <CalendarIcon />
          <span>{getEventSchedule(event)}</span>
        </div>
        {/* Animated title container */}
        <div className="relative flex items-center">
          {/* Animated Arrow */}
          <motion.div
            variants={arrowVariants}
            className="absolute left-0"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="text-white">
              <path d="M13.75 6.75L19.25 12L13.75 17.25" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M19 12H4.75" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </motion.div>
          {/* Animated Event Name */}
          <motion.h3
            variants={textVariants}
            className="text-2xl font-medium text-white"
          >
            {event.name}
          </motion.h3>
        </div>
        <p className="text-gray-400 text-sm leading-relaxed mt-2">{event.description}</p>
      </div>
    </motion.div>
  );
};

export default EventCard;
